import { useState } from 'react'
import { useSubscription } from '../hooks/useSubscription.js'
import PlanSelectModal from './PlanSelectModal.jsx'
import StatusPill from './StatusPill.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'
import { formatMoney } from '../lib/format.js'

// Plan / billing card for SettingsScreen.
//
// Shows the customer:
//   - their current plan (display name + price)
//   - trial countdown if trialing
//   - status (active, past_due, canceled)
//   - Manage billing button (Stripe customer portal)
//   - Upgrade / Choose a plan button (opens PlanSelectModal)
//
// Pre-Stripe-setup: the card still renders. With no subscription
// row, it shows the "Choose a plan" CTA. Clicking it tries to start
// checkout; if Stripe isn't configured yet, the error message is
// clear ("Billing isn't set up yet — the app owner needs to add a
// Stripe key.") so Scott / a beta tester knows what's happening.
export default function SubscriptionCard() {
  const sub = useSubscription()
  const [planModalOpen, setPlanModalOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Remember which action failed so the error box can offer a manual
  // "Try again". 'manage' re-invokes handleManage(); a checkout object
  // re-invokes startCheckout with the same tier/billing. Re-requesting
  // only fetches a Checkout session URL (no charge), so retry is safe.
  const [lastAction, setLastAction] = useState(null)

  async function handleManage() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const url = await sub.openCustomerPortal()
      window.location.href = url
    } catch (e) {
      // If the portal failed because there's no Stripe customer yet
      // (no_subscription), re-hitting the portal will just fail again.
      // Route the retry to the plan picker instead. The hook attaches the
      // raw server code as e.code (read in JS logic only, never rendered),
      // so we branch on that rather than coupling to the display copy.
      // Network/abort errors carry code=null and fall through to 'manage'.
      if (e?.code === 'no_subscription') {
        setLastAction('choose-plan')
      } else {
        setLastAction('manage')
      }
      setError(e?.message || 'Could not open billing portal.')
    } finally {
      setBusy(false)
    }
  }

  async function startCheckoutFlow({ tierId, billing }) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const url = await sub.startCheckout({ tierId, billing })
      window.location.href = url
    } catch (e) {
      setLastAction({ type: 'checkout', tierId, billing })
      setError(e?.message || 'Could not start checkout.')
      // Close the modal so the card-level error + retry is visible.
      setPlanModalOpen(false)
    } finally {
      setBusy(false)
    }
  }

  function retryLastAction() {
    if (busy || !lastAction) return
    if (lastAction === 'manage') {
      handleManage()
    } else if (lastAction === 'choose-plan') {
      // No Stripe customer behind "Manage billing" — send them to pick
      // a plan instead of re-hitting the portal that would fail again.
      setError(null)
      setPlanModalOpen(true)
    } else if (lastAction?.type === 'checkout') {
      startCheckoutFlow({ tierId: lastAction.tierId, billing: lastAction.billing })
    }
  }

  if (sub.loading) {
    return (
      <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
        <div className="text-sm text-black/55">Loading plan…</div>
      </section>
    )
  }

  const summary = sub.summary
  const isActive = sub.isActive
  const tierName = summary?.display_name || 'Trial'
  const monthly  = summary?.monthly_cents ? formatMoney(summary.monthly_cents / 100) : null

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-black/55">
            Plan & billing
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <h2 className="text-xl font-medium">{tierName}</h2>
            {summary?.founder_cohort_locked && (
              <span className="inline-flex items-center rounded-full bg-amber-brand-50 px-2 py-0.5 text-[11px] font-medium text-amber-brand-800">
                Founder · locked
              </span>
            )}
          </div>
          {monthly && (
            <div className="mt-0.5 text-sm text-black/65">
              {monthly}/{summary?.is_annual ? 'year' : 'month'}
              {!isActive && summary?.status === 'canceled' && ' · canceled'}
              {summary?.status === 'past_due' && ' · payment failed'}
            </div>
          )}
        </div>
        <StatusBadge status={summary?.status} trialDaysLeft={sub.trialDaysLeft} />
      </div>

      {/* Trial countdown — encouraging, not alarming. */}
      {summary?.status === 'trialing' && sub.trialDaysLeft != null && (
        <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {sub.trialDaysLeft > 0
            ? <>You're in your free trial — <strong>{sub.trialDaysLeft} day{sub.trialDaysLeft === 1 ? '' : 's'} left.</strong> Add a card any time to keep using the app after.</>
            : <>Your trial ends today. Add a card to keep going.</>}
        </div>
      )}

      {summary?.cancel_at_period_end && (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Subscription will cancel on{' '}
          {summary.current_period_end ? new Date(summary.current_period_end).toLocaleDateString() : 'the next billing date'}.
          You can resume any time before then.
        </div>
      )}

      {/* Payment-failed banner — renders for past_due / unpaid, BUT only
          when the subscription is NOT already set to cancel. The three
          banners are NOT mutually exclusive: cancel_at_period_end is an
          independent boolean, so a sub can be past_due AND canceling at
          once. Precedence: the cancel banner (above) wins — once a
          customer has chosen to cancel, we don't also nag them to update
          their card. The trial banner is trialing-only. The "Update
          payment" button reuses the existing busy-gated handleManage()
          so there's no new submit path and no double-submit risk. */}
      {(summary?.status === 'past_due' || summary?.status === 'unpaid') && !summary?.cancel_at_period_end && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span>Your last payment didn't go through — update your card to keep your account active.</span>
          <button
            type="button"
            disabled={busy}
            onClick={handleManage}
            className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Update payment'}
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
          <span>{error}</span>
          {lastAction && (
            <button
              type="button"
              disabled={busy}
              onClick={retryLastAction}
              className="rounded-md bg-white/15 px-2.5 py-1 text-xs font-medium text-white hover:bg-white/25 disabled:opacity-50"
            >
              {busy ? 'Trying…' : lastAction === 'choose-plan' ? 'Choose a plan' : 'Try again'}
            </button>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!summary && (
          <button
            type="button"
            onClick={() => setPlanModalOpen(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Choose a plan
          </button>
        )}
        {/* Show Manage billing whenever a subscription exists in any
            state — trialing, active, past_due, even canceled (so they
            can resume). The portal endpoint looks up the Stripe
            customer id server-side, so we don't need it surfaced here. */}
        {summary && (
          <button
            type="button"
            disabled={busy}
            onClick={handleManage}
            className="rounded-lg border border-black/15 bg-white px-4 py-2 text-sm font-medium text-black/80 hover:bg-black/5 disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Manage billing'}
          </button>
        )}
        {isActive && summary?.tier_id !== 'pro' && (
          <button
            type="button"
            onClick={() => setPlanModalOpen(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Change plan
          </button>
        )}
        <a
          href="/pricing"
          className="rounded-lg border border-black/15 bg-white px-4 py-2 text-sm font-medium text-black/65 hover:bg-black/5"
        >
          See pricing →
        </a>
      </div>

      <PlanSelectModal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        tiers={sub.tiers}
        currentTierId={summary?.tier_id}
        onSelect={({ tierId, billing }) => startCheckoutFlow({ tierId, billing })}
      />
    </section>
  )
}

function StatusBadge({ status, trialDaysLeft }) {
  if (!status) {
    return <StatusPill tone="blue">No plan</StatusPill>
  }
  // Trial state gets a custom label that includes the day countdown,
  // so we override the default labelFor() value here. Everything else
  // routes through the centralized invoice domain table.
  const label = status === 'trialing' && trialDaysLeft != null
    ? `Trial · ${trialDaysLeft}d`
    : labelFor('invoice', status)
  return <StatusPill tone={toneFor('invoice', status)}>{label}</StatusPill>
}
